// Turns the raw card database into the CardDef records the engine reads.
//
// The engine never touches cards.json directly: it works from CardDefs, which
// are normalised, have their keywords already parsed, and can also be built by
// hand for tokens and tests.

import type { CardData, Rarity as DataRarity } from '../cards/types.ts';
import { HIDDEN_CARD } from './redact.ts';
import { parseKeywords, hasProse } from './keywords.ts';
import type { CardDef, CardType, Cost, Domain, Rarity } from './types.ts';

/**
 * 131. A card's printed cost is an Energy numeral plus some number of Power
 * pips. The pips are [C] -- Power of the card's own domain -- so a dual-domain
 * card can pay them with either of its domains (135.2.e.6.c), and a domainless
 * card's pips become [A], payable by any domain (135.2.e.6.b).
 */
function costOf(data: CardData): Cost {
  return {
    energy: data.energy ?? 0,
    power: data.power ?? 0,
    powerDomains: [...data.domains],
  };
}

/**
 * 133.8.b. The champion tag links a legend to its champion units and signature
 * cards. Riot's data lists every tag a card has -- champion, faction, species --
 * so the champion tag is the one shared with a Champion Legend rather than a
 * fixed position in the list. `CardIndex` resolves it once the whole set is
 * known; on its own a card cannot tell which of its tags is the champion.
 */
export function championTagOf(data: CardData, championTags: ReadonlySet<string>): string | null {
  for (const tag of data.tags) {
    if (championTags.has(tag)) return tag;
  }
  return null;
}

const RARITY: Record<DataRarity, Rarity> = {
  common: 'common', uncommon: 'uncommon', rare: 'rare',
  epic: 'epic', showcase: 'showcase',
};

export function toCardDef(data: CardData, championTags: ReadonlySet<string> = new Set()): CardDef {
  const parsed = parseKeywords(data.text);
  const effect = parseKeywords(data.effect);
  return {
    id: data.id,
    name: data.name,
    tag: championTagOf(data, championTags),
    tags: [...data.tags],
    type: data.type as CardType,
    superType: data.superType,
    rarity: RARITY[data.rarity] ?? 'common',
    domains: [...data.domains] as Domain[],
    cost: costOf(data),
    might: data.might,
    mightBonus: data.mightBonus,
    text: data.text ?? '',
    effectText: data.effect ?? '',
    // 136.2.c. Effect text is appended to whatever the equipment attaches to,
    // so its keywords belong to the host, not to the gear sitting in a base.
    keywords: parsed.keywords,
    keywordValues: parsed.values,
    hasUnimplementedText: hasProse(parsed.body) || hasProse(effect.body),
  };
}

/** Lookup for every printing in the game, keyed by card id. */
export class CardIndex {
  private readonly defs = new Map<string, CardDef>();

  constructor(cards: readonly CardData[]) {
    // A Champion Legend names the champion it belongs to (103.1), so the set of
    // champion tags is exactly the tags those legends carry. Collecting them
    // first is what lets every other card work out which of its own tags is the
    // champion one.
    const championTags = new Set<string>();
    for (const c of cards) {
      if (c.superType === 'champion' && c.type === 'legend') {
        for (const tag of c.tags) championTags.add(tag);
      }
    }

    for (const c of cards) this.defs.set(c.id, toCardDef(c, championTags));
    // 187. The printed token always wins. The builtins are a stand-in for the
    // handful the card gallery does not list, and if one of those is ever
    // published the real card -- with its art, its tags and its text -- should
    // take over without anyone having to remember to delete the stand-in.
    for (const def of BUILTIN_TOKENS) {
      if (!this.defs.has(def.id)) this.defs.set(def.id, def);
    }

    // The card a player is not allowed to identify. Registered here so every
    // path that draws a card keeps working on a redacted state without a
    // special case -- the layout, the texture streamer and the card back all
    // simply look it up. It has no art, so the deck builder never offers it
    // and it cannot end up in a deck.
    this.defs.set(HIDDEN_CARD, {
      id: HIDDEN_CARD,
      name: 'Hidden',
      tag: null,
      tags: [],
      // Unknown type wears the main deck's back, which is what a face-down
      // card in a hand or a deck looks like from across the table.
      type: 'spell',
      superType: null,
      rarity: 'common',
      domains: [],
      cost: { energy: 0, power: 0, powerDomains: [] },
      might: null,
      mightBonus: null,
      text: '',
      effectText: '',
      keywords: [],
      keywordValues: {},
      hasUnimplementedText: false,
    });
  }

  get(id: string): CardDef {
    const def = this.defs.get(id);
    if (!def) throw new Error(`unknown card: ${id}`);
    return def;
  }

  has(id: string): boolean {
    return this.defs.has(id);
  }

  all(): CardDef[] {
    return [...this.defs.values()];
  }

  filter(pred: (c: CardDef) => boolean): CardDef[] {
    return this.all().filter(pred);
  }
}

// -- tokens ------------------------------------------------------------------

/**
 * 187. Token bodies the engine defines itself.
 *
 * Empty, and meant to stay that way. Every token is a real card: the compiler
 * names them by their printed ids, so a created token arrives with that card's
 * artwork, text, keywords and -- the half that is invisible until an aura
 * misses -- its *tags*. Bodies invented here instead carried none, so a Mech
 * token was not tagged Mech and "your Mechs each have [Assault]" passed
 * straight over the Mechs that card had just made.
 *
 * The handful the card gallery does not list are added to the card data by the
 * pipeline (`tools/unlisted-tokens.mjs`), which keeps one source of truth for
 * a card's body rather than two that can drift.
 */
export const BUILTIN_TOKENS: readonly CardDef[] = [];

// -- derived questions the engine asks a lot ---------------------------------

export const isPermanentType = (t: CardType): boolean =>
  t === 'unit' || t === 'champion' || t === 'gear' || t === 'token';

/** 141. Champion units and unit tokens are units for every rules purpose. */
export const isUnitType = (t: CardType): boolean =>
  t === 'unit' || t === 'champion' || t === 'token';

export const isSpellType = (t: CardType): boolean =>
  t === 'spell' || t === 'signatureSpell';

export const hasKeyword = (def: CardDef, kw: CardDef['keywords'][number]): boolean =>
  def.keywords.includes(kw);

export const keywordValue = (def: CardDef, kw: CardDef['keywords'][number]): number =>
  def.keywordValues[kw] ?? (def.keywords.includes(kw) ? 1 : 0);

/**
 * 103.1.b.4. A card is legal in a Domain Identity if every domain it has is in
 * that identity. Domainless cards fit any identity.
 */
export function fitsIdentity(def: CardDef, identity: readonly Domain[]): boolean {
  return def.domains.every((d) => identity.includes(d));
}
